import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { NewsletterPopup } from "./components/NewsletterSignup";
import SEOHead, { getLocalBusinessSchema, getWebsiteSchema, getFAQSchema } from "./components/SEOHead";

// Pages
import HomePage from "./pages/HomePage";
import AnalysisPage from "./pages/AnalysisPage";
import PhotoAnalysisPage from "./pages/PhotoAnalysisPage";
import SpinTouchPage from "./pages/SpinTouchPage";
import ChatPage from "./pages/ChatPage";
import ShopPage from "./pages/ShopPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import AuthPage from "./pages/AuthPage";
import AppointmentPage from "./pages/AppointmentPage";
import ProPage from "./pages/ProPage";
import PoolConfigPage from "./pages/PoolConfigPage";
import AdminPage from "./pages/AdminPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import WeatherPage from "./pages/WeatherPage";
import MyAnalysesPage from "./pages/MyAnalysesPage";
import MyPoolPage from "./pages/MyPoolPage";
import NotificationsPage from "./pages/NotificationsPage";
import BlogPage from "./pages/BlogPage";
import ArticlePage from "./pages/ArticlePage";
import MySpinTouchPage from "./pages/MySpinTouchPage";
import CGVPage from "./pages/CGVPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import ZonePage from "./pages/ZonePage";
import ZonesPage from "./pages/ZonesPage";
import CalculatorPage from "./pages/CalculatorPage";
import SiteReviewsPage from "./pages/SiteReviewsPage";
import AdminLoyaltyScanPage from "./pages/AdminLoyaltyScanPage";

function AppContent() {
    const location = useLocation();
    
    // Check for OAuth callback with session_id in hash
    if (location.hash?.includes('session_id=') && location.pathname === '/auth') {
        return <AuthPage />;
    }

    return (
        <div className="flex flex-col min-h-screen">
            {/* Global SEO with structured data */}
            <SEOHead 
                structuredData={[
                    getLocalBusinessSchema(),
                    getWebsiteSchema(),
                    getFAQSchema()
                ]}
            />
            <Navbar />
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/analysis" element={<AnalysisPage />} />
                    <Route path="/analysis/photo" element={<PhotoAnalysisPage />} />
                    <Route path="/analysis/spintouch" element={<SpinTouchPage />} />
                    <Route path="/analysis/history" element={<AnalysisPage />} />
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/avis" element={<SiteReviewsPage />} />
                    <Route path="/shop" element={<ShopPage />} />
                    <Route path="/shop/:productId" element={<ProductDetailPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                    <Route path="/order-success" element={<OrderSuccessPage />} />
                    <Route path="/orders" element={<OrderTrackingPage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/appointment" element={<AppointmentPage />} />
                    <Route path="/pro" element={<ProPage />} />
                    <Route path="/pool-config" element={<PoolConfigPage />} />
                    <Route path="/my-pool" element={<MyPoolPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/loyalty" element={<LoyaltyPage />} />
                    <Route path="/admin/loyalty/scan/:token" element={<AdminLoyaltyScanPage />} />
                    <Route path="/weather" element={<WeatherPage />} />
                    <Route path="/my-analyses" element={<MyAnalysesPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<ArticlePage />} />
                    <Route path="/spintouch" element={<MySpinTouchPage />} />
                    <Route path="/my-spintouch" element={<MySpinTouchPage />} />
                    <Route path="/cgv" element={<CGVPage />} />
                    <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                    <Route path="/zones" element={<ZonesPage />} />
                    <Route path="/zone/:zone" element={<ZonePage />} />
                    <Route path="/calculateur" element={<CalculatorPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                </Routes>
            </main>
            <Footer />
            <Toaster position="top-center" richColors />
            <NewsletterPopup delay={8000} />
        </div>
    );
}

function App() {
    return (
        <HelmetProvider>
            <ThemeProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <CartProvider>
                            <AppContent />
                        </CartProvider>
                    </AuthProvider>
                </BrowserRouter>
            </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;
